/**
 * Which festival is this page for?
 *
 * Resolved from the hostname against the configured platform domain. The
 * domain is NEVER hardcoded — it arrives as VITE_PLATFORM_DOMAIN, so the
 * platform can be rehomed without a code change, and `ozora.localhost` in
 * development behaves exactly as `ozora.myrave.io` will in production.
 */
import { LANG_PARAM, localeFromParam } from '@myrave/shared';
export interface TenantHint {
  /** The festival subdomain, or null on the bare platform domain. */
  subdomain: string | null;
}

export { RESERVED_SUBDOMAINS } from './raveHost';
import { RESERVED_SUBDOMAINS, raveOfHost } from './raveHost';

export function platformDomain(): string {
  return import.meta.env.VITE_PLATFORM_DOMAIN ?? 'localhost';
}

export function apiUrl(): string {
  return import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
}

export function resolveTenant(hostname = window.location.hostname): TenantHint {
  return { subdomain: raveOfHost(hostname, platformDomain()) };
}

/**
 * Is this host's page a PUBLIC one (step 73.11)? The bare platform domain
 * (the public site, its artist pages, the release notes) and every rave's own
 * address (its landing page, signed out) — never `app.`, `login.`, the
 * consoles or any other reserved host. Search engines get every language's
 * address on these (`worker/languageAlternates.ts`), and their links carry
 * `?lang=` (`app/languageAlternates.tsx`). The Worker passes its own domain.
 */
export function isPublicPageHost(host: string, platform: string = platformDomain()): boolean {
  const h = host.toLowerCase();
  const p = platform.toLowerCase();
  if (h === p) return true;
  if (!h.endsWith(`.${p}`)) return false;
  const label = h.slice(0, -(p.length + 1));
  return Boolean(label) && !label.includes('.') && !RESERVED_SUBDOMAINS.has(label);
}

/**
 * The canonical URL for a festival, built from config rather than a literal.
 *
 * The current port is carried over so `ozora.localhost:5173` works in
 * development; in production `window.location.port` is empty and none is
 * appended.
 */
export function buildFestivalUrl(
  subdomain: string, domain: string, protocol: string, port: string,
): string {
  return `${protocol}//${subdomain}.${domain}${port ? `:${port}` : ''}`;
}

/**
 * Is this the APPLICATION host (`app.myrave.io`) rather than the public site?
 *
 * Both resolve to `{ subdomain: null }` — `app` is a reserved prefix, so it is
 * never a festival — but they are two different products. The bare domain is
 * the public marketing site that anyone may read; `app.` is where somebody
 * who has an account actually works. Keeping them apart is what lets the front
 * page have a "Log in" button rather than an account chip.
 */
export function isAppHost(hostname = window.location.hostname): boolean {
  const host = hostname.toLowerCase();
  const domain = platformDomain().toLowerCase();
  return host === `app.${domain}`;
}

/**
 * Is this the ACCOUNT host, `account.myrave.io`?
 *
 * A host of its own at the user's instruction (2026-09-24), and it earns one
 * for the same reason `login.` has one: it is a place, not a page of the app.
 * Somebody can bookmark it, a password manager can recognise it, and "where
 * do I change my details" has an answer you can say out loud.
 *
 * It is not a tenant — `account` is reserved, so it can never be a festival's
 * subdomain — and it needs a session like every other host but the bare
 * domain and `login.`.
 */
export function isAccountHost(hostname = window.location.hostname): boolean {
  const host = hostname.toLowerCase();
  return host === `account.${platformDomain().toLowerCase()}`;
}

/** The account — `account.` on the platform domain. */
export function accountUrl(path = '/'): string {
  const { protocol, port } = window.location;
  return `${protocol}//account.${platformDomain()}${port ? `:${port}` : ''}${path}`;
}

/**
 * Is this the RAVE ADMIN host, `radm.myrave.io`? (`plan.md` §65)
 *
 * Where a festival is administered, as against `/admin`, which keeps the
 * PLATFORM's own business — approvals, sign-in methods, creating a rave. The
 * database has drawn that line since the schema was written: `User.role` is
 * platform-wide, `FestivalAdmin` is per festival. Only the UI conflated them.
 */
export function isRaveAdminHost(hostname = window.location.hostname): boolean {
  const host = hostname.toLowerCase();
  return host === `radm.${platformDomain().toLowerCase()}`;
}

/** The rave admin — `radm.` on the platform domain. */
export function raveAdminUrl(path = '/'): string {
  const { protocol, port } = window.location;
  return `${protocol}//radm.${platformDomain()}${port ? `:${port}` : ''}${path}`;
}

/**
 * Is this the PLATFORM console, `appadm.myrave.io`? (`plan.md` §66)
 *
 * Every festival and every account, for a `SUPER_ADMIN`. `radm.` is one
 * festival's; this is all of them. The host decides where the screens live
 * and nothing else — the role is checked at the API on every route.
 */
export function isConsoleHost(hostname = window.location.hostname): boolean {
  const host = hostname.toLowerCase();
  return host === `appadm.${platformDomain().toLowerCase()}`;
}

/** The live dashboard's host, `ops.` (step 97) — the platform's figures on one screen. */
export function isOpsHost(hostname = window.location.hostname): boolean {
  return hostname.toLowerCase() === `ops.${platformDomain().toLowerCase()}`;
}

/** The live dashboard — `ops.` on the platform domain. */
export function opsUrl(path = '/'): string {
  const { protocol, port } = window.location;
  return `${protocol}//ops.${platformDomain()}${port ? `:${port}` : ''}${path}`;
}

/** The platform console — `appadm.` on the platform domain. */
export function consoleUrl(path = '/'): string {
  const { protocol, port } = window.location;
  return `${protocol}//appadm.${platformDomain()}${port ? `:${port}` : ''}${path}`;
}

/**
 * Is this the SUPPORT host, `support.myrave.io`? (the user, 2026-10-21:
 * "support page should live under support.* sub domain")
 *
 * The Support page and nothing else, signed in or NOT — somebody who cannot
 * sign in is the first who needs to write. `support` has always been
 * reserved, so it was never a festival's.
 */
export function isSupportHost(hostname = window.location.hostname): boolean {
  const host = hostname.toLowerCase();
  return host === `support.${platformDomain().toLowerCase()}`;
}

/** The Support page — `support.` on the platform domain. `path` may carry `?topic=` / `?rave=`. */
export function supportUrl(path = '/'): string {
  const { protocol, port } = window.location;
  return `${protocol}//support.${platformDomain()}${port ? `:${port}` : ''}${path}`;
}

/**
 * Report something wrong in a rave (the 0.14.82 walk, w-sp-reports: "i can't find any report buttons" — a set's time,
 * an artist not playing this rave, the music, the rave's details): the Support Center's form with the topic "A rave",
 * the rave, the part of it (and the problem, when it is known) and what it is about already filled in.
 */
export function raveReportUrl(r: { rave: string; editionId?: string; area: string; issue?: string; about?: string }): string {
  const q = new URLSearchParams({ topic: 'rave', rave: r.rave, area: r.area });
  if (r.editionId) q.set('edition', r.editionId);
  if (r.issue) q.set('issue', r.issue);
  if (r.about) q.set('about', r.about);
  return supportUrl(`/?${q.toString()}`);
}

/** Claiming an artist page: the Support Center's form, its topic and the artist filled in (the 0.14.74 walk). */
export function artistClaimUrl(slug: string): string {
  return supportUrl(`/?topic=artist-claim&artist=${encodeURIComponent(slug)}`);
}

/** Is this the sign-in host, `login.myrave.io`? */
export function isLoginHost(hostname = window.location.hostname): boolean {
  const host = hostname.toLowerCase();
  return host === `login.${platformDomain().toLowerCase()}`;
}

/**
 * The sign-in host, and nothing after it.
 *
 * **No `?next=`, deliberately.** A password page whose address bar reads
 * exactly `login.myrave.io` is one somebody can look at and trust; the same
 * page trailing an encoded URL is the shape of every phishing link they have
 * been told to watch for.
 *
 * There is no destination parameter at all — signing in always lands on
 * `app.`, so there is nothing caller-supplied to validate and no
 * open-redirect surface to guard.
 */
export function loginUrl(): string {
  const { protocol, port, search } = window.location;
  // The language this address names goes with it (`?lang=`, 2026-10-27): a heart's sign-in and
  // `RequireSession` NAVIGATE, so the click-time carrying in `languageAlternates.tsx` never saw them.
  const lang = localeFromParam(new URLSearchParams(search).get(LANG_PARAM));
  return `${protocol}//login.${platformDomain()}${port ? `:${port}` : ''}/${lang ? `?${LANG_PARAM}=${lang}` : ''}`;
}

/**
 * The phone app's permanent install link — `android.` (the install page, the newest APK) or `apple.`
 * (TestFlight) on the platform domain (`worker/installLinks.ts`, frontend/CLAUDE.md "Install links").
 */
export function installUrl(phone: 'android' | 'apple'): string {
  const { protocol, port } = window.location;
  return `${protocol}//${phone}.${platformDomain()}${port ? `:${port}` : ''}/`;
}

/** The application — `app.` on the platform domain. */
export function appUrl(path = '/'): string {
  const { protocol, port } = window.location;
  return `${protocol}//app.${platformDomain()}${port ? `:${port}` : ''}${path}`;
}

/**
 * Where signing out lands: **the page you were on** (the user, 2026-10-29, walking 0.14.69: "sign
 * out now brings you to a different page and not where you were") — wherever that page can be
 * seen signed out, on the web and in the phone app alike; a page that needs an account gives way
 * to the nearest one that does not. Replaces 2026-10-16's "a rave's home, else the platform's" and
 * 2026-10-26's "Find a rave in the phone app" (`plan.md` §6.14).
 *
 * - **A rave** (`<rave>.<platform>/<edition>/<section>`) — read signed out since 2026-10-26: the
 *   same page, except your own ones (My lineup, Rides, Meet people, Photos, My tickets), which give
 *   way to the rave's home.
 * - **The main app** — Upcoming raves, Artists, an artist, What's new stay; every page that needs
 *   an account (My raves, All raves, Messages, Plans…) gives way to Upcoming raves.
 * - **The bare platform domain** — every page there is public: it stays (the phone app shows none
 *   of them, so it goes to the app's first page).
 * - **The Support Center** — its form; your tickets and the team's pages need an account.
 * - **The account, the consoles** — all behind an account: Upcoming raves, or — from a rave's
 *   admin — that rave itself.
 *
 * Only the PATH is kept, never the query or the hash (a `#save=`, `?checkout=`…), apart from a
 * valid `?lang=`; and only a path of plain labels, so nothing in the address can pick the page.
 */
export function afterSignOutUrl(
  native: boolean,
  loc: Pick<Location, 'hostname' | 'origin' | 'pathname'> & { search?: string } = window.location,
): string {
  const host = loc.hostname.toLowerCase();
  const domain = platformDomain().toLowerCase();
  const parts = loc.pathname.split('/').filter(Boolean);
  const plain = parts.every((p) => SIGNED_OUT_LABEL.test(p));
  const lang = localeFromParam(new URLSearchParams(loc.search ?? '').get(LANG_PARAM));
  const here = (path: string) => `${loc.origin}${path}${lang ? `?${LANG_PARAM}=${lang}` : ''}`;
  const path = (segments: string[]) => `/${segments.join('/')}`;

  if (resolveTenant(host).subdomain) {
    const [edition, section] = plain ? parts : [];
    if (!edition || (parts.length === 1 && OWN_RAVE_SECTIONS.has(edition))) return here('/');
    if (parts.length === 2 && section && !OWN_RAVE_SECTIONS.has(section)) return here(path(parts));
    return here(`/${edition}`);
  }
  if (host === `app.${domain}`) {
    const open = parts.length === 0
      || (parts.length <= 2 && parts[0] === 'artists')
      || (parts.length === 1 && parts[0] === 'whats-new');
    return here(plain && open ? path(parts) : '/');
  }
  if (host === domain) return native ? appUrl('/') : here(plain && parts.length <= 2 ? path(parts) : '/');
  if (host === `support.${domain}`) return here('/');
  if (host === `radm.${domain}` && plain && parts.length >= 2) return festivalUrl(parts[0]!, parts[1]!);
  return appUrl('/');
}

/** One label of a path a sign-out may keep — letters, digits, dashes; never `..` or an escape. */
const SIGNED_OUT_LABEL = /^[a-z0-9-]{1,63}$/i;

/** A rave's pages that are YOURS — signed out they ask for the sign-in, so a sign-out leaves them. */
const OWN_RAVE_SECTIONS: ReadonlySet<string> = new Set(['lineup', 'rides', 'dating', 'photos', 'tickets']);

/** The discovery directory — the bare platform domain. */
export function platformUrl(): string {
  const { protocol, port } = window.location;
  return `${protocol}//${platformDomain()}${port ? `:${port}` : ''}`;
}

/**
 * The privacy policy or the terms of use on the bare platform domain, in the
 * reader's language (2026-10-27): `?lang=<locale>` — the Worker serves the
 * translation when it has one (`worker/legal/index.ts`), else the English.
 */
export function legalUrl(page: 'privacy' | 'terms', locale?: string | null): string {
  return `${platformUrl()}/${page}${locale ? `?lang=${encodeURIComponent(locale)}` : ''}`;
}

/**
 * A rave's address — and, given an edition slug, THAT year's address.
 *
 * `festivalUrl('ozora')` is `https://ozora.myrave.io`, which opens whatever
 * edition tenancy picks (running or next, else the most recent past).
 * `festivalUrl('ozora', '2026')` is `https://ozora.myrave.io/2026`, which opens
 * 2026 whatever the date. Every link that knows WHICH edition it means passes
 * the slug: a 2026 set on an artist's page must open 2026, not the year the
 * bare subdomain happens to point at by then.
 */
export function festivalUrl(subdomain: string, editionSlug?: string | null): string {
  const { protocol, port } = window.location;
  const base = buildFestivalUrl(subdomain, platformDomain(), protocol, port);
  return editionSlug ? `${base}/${encodeURIComponent(editionSlug)}` : base;
}

/**
 * The edition slug the address names, if it names one of `slugs`.
 *
 * The FIRST path segment only, and only when it is one of the festival's
 * published editions — anything else (`/`, `/reset-password`, a year that was
 * never published) is not a year and leaves the default in place. Hash routes
 * (`#day=`, `#act=`) ride after it untouched.
 */
export function editionSlugFromPath(
  slugs: readonly string[], pathname = window.location.pathname,
): string | null {
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first) return null;
  let segment: string;
  try { segment = decodeURIComponent(first); } catch { return null; }
  return slugs.includes(segment) ? segment : null;
}
